import React, { useState, useCallback, useEffect } from "react";
import dayjs from "dayjs";
import SinglePageContainer from "../../../Common/SinglePageContainer";
import {
  Button,
  Segmented,
  message,
  Spin,
  Dropdown,
  MenuProps,
  Typography,
  Badge,
  Col,
  Row,
} from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EllipsisOutlined,
  ExclamationCircleFilled,
  EyeOutlined,
  HomeOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { Survey } from "../../../../types/survey";
import { SurveyBuilder } from "../../../Common/Survey/SurveyBuilder";
import {
  SurveyStatus,
  useSurveyCreateFull,
  useSurveyDelete,
  useSurveyDownload,
  useSurveyGetById,
  useSurveyUpdate,
  useTemplateCreate,
} from "../../../../hooks/api/surveys";
import { NavLink, useLocation, useNavigate, useParams } from "react-router";
import { adaptApiSurvey } from "../../../../utils/surveyAdapter";
import { useBreadcrumbs } from "../../../../context/breadcrumbs";
import { SurveyPreview } from "../../../Common/SurveyPreview";
import { useSimpleModal } from "../../../Common/Modal/Simple";

import styles from "./index.module.scss";
import { getSurveyStatusColor } from "..";
import {
  dateFormat,
  dateTimeFormat,
  dateTimeFormatRevert,
  timeFormatFull,
} from "../../../../contstant";
import {
  transformApiSurveyToApp,
  transformSurveyToApi,
} from "../../../../utils/transformSurveyToApi";
import { useMessageError, useMessageSuccess } from "../../../../hooks/common";
import { cloneSurveyWithNewIds } from "../../../../utils/cloneSurveyForTemplate";
import Loading from "../../../Common/Loading";
import StatusIndicator from "../../../Common/StatusIndicator";
import isEqual from "lodash/isEqual";
import { useUnsavedChangesGuard } from "../../../../utils/useUnsavedChangesGuard";
import { downloadFromAnchor } from "../../../../utils";

const { Text, Title } = Typography;

const createEmptySurvey = (): Survey => ({
  status: "draft",
  title: "",
  items: [],
});

export default function SurveyEditContent(): React.ReactNode | null {
  const [mode, setMode] = useState<"add" | "preview">("add");
  const { id } = useParams();
  const navigate = useNavigate();
  const { open, contextHolder } = useSimpleModal();
  const { setBreadcrumbs } = useBreadcrumbs();
  const surveyGetById = useSurveyGetById(id);
  const surveyUpdate = useSurveyUpdate();
  const surveyDelete = useSurveyDelete(id);
  const surveyDownload = useSurveyDownload(id);
  const templateCreate = useTemplateCreate();
  const location = useLocation();
  const fromPage = location.state?.fromPage || "1";
  const [surveyData, setSurveyData] = useState<Survey>(createEmptySurvey());
  const [originalData, setOriginalData] = useState<Survey | null>(null);

  console.log(surveyData);

  useEffect(() => {
    setBreadcrumbs([
      { title: <HomeOutlined />, href: "/" },
      { title: "Survey", href: "/survey" },
      { title: "Edit" },
    ]);
  }, []);

  const isDirty = originalData
    ? !isEqual(
      { ...surveyData, updatedAt: undefined },
      { ...originalData, updatedAt: undefined },
    )
    : false;

  useUnsavedChangesGuard(isDirty, () => { }, {
    open: (config) =>
      open({
        ...config,
        okButtonProps: { danger: true },
        icon: <ExclamationCircleFilled />,
      }),
  });

  useEffect(() => {
    if (surveyGetById.data && !originalData) {
      const transformed = transformApiSurveyToApp(surveyGetById.data);
      setSurveyData(transformed);
      setOriginalData(transformed);
    }
  }, [surveyGetById.data]);

  useEffect(() => {
    if (!surveyDelete.error && !surveyDelete.loading && surveyDelete.data) {
      navigate(`/survey?current=${fromPage}`);
    }
  }, [
    surveyDelete.error,
    surveyDelete.loading,
    surveyDelete.data,
    fromPage,
    navigate,
  ]);

  const handleSurveyChange = useCallback((survey: Survey) => {
    setSurveyData(survey);
  }, []);

  const handleSave = useCallback(async () => {
    if (!surveyData.title.trim()) {
      message.error("Please enter survey title");
      return;
    }
    const apiData = transformSurveyToApi({
      ...surveyData,
      status: "draft",
    });
    try {
      const res = await surveyUpdate.fetch(apiData, id);
      if (res?.id) {
        message.success("Survey saved as draft!");
      }
      setOriginalData(surveyData);
    } catch (err: any) {
      message.error(err?.message || "Failed to save survey");
      console.error(err);
    }
  }, [surveyData, surveyUpdate, id]);

  const handleSaveTemplate = useCallback(async () => {
    const surveyClone = cloneSurveyWithNewIds(surveyData);

    const apiData = transformSurveyToApi({
      ...surveyClone,
      status: "draft",
    });

    try {
      const res = await templateCreate.fetch(apiData);

      if (res?.id) {
        message.success("Template created");
      }
      setOriginalData(surveyData);
    } catch (err: any) {
      message.error(err?.message || "Failed to save template");
    }
  }, [surveyData, templateCreate]);

  const handlePublish = useCallback(async () => {
    if (!surveyData.title.trim()) {
      message.error("Please enter survey title");
      return;
    }
    if (surveyData.items.length === 0) {
      message.error("Please add at least one question or section");
      return;
    }
    const apiData = transformSurveyToApi({
      ...surveyData,
      status: "published",
    });
    try {
      const res = await surveyUpdate.fetch(apiData, id);
      if (res?.id) {
        message.success("Survey published successfully!");
      }
      setOriginalData(surveyData);
    } catch (err: any) {
      message.error(err?.message || "Failed to publish survey");
      console.error(err);
    }
  }, [surveyData, surveyUpdate, id, surveyGetById]);

  useEffect(() => {
    if (!surveyUpdate.error && !surveyUpdate.loading && surveyUpdate.data) {
      surveyGetById.fetch().then((res) => {
        const transformed = transformApiSurveyToApp(res);
        setSurveyData(transformed);
        setOriginalData(transformed);
      });
    }
  }, [surveyUpdate.data, surveyUpdate.loading, surveyUpdate.error]);

  const handleModeChange = useCallback((value: "add" | "preview") => {
    setMode(value);
  }, []);
  const items: MenuProps["items"] = [
    {
      key: "download",
      type: "item",
      icon: <DownloadOutlined />,
      label: "Download",
      onClick: () => {
        surveyDownload.fetch().then((response) => {
          if (!response) return;
          downloadFromAnchor(
            response,
            `${surveyGetById.data?.title}`,
            "application/pdf",
          );
        });
      },
    },
    {
      key: "delete",
      type: "item",
      icon: <DeleteOutlined style={{ color: "red" }} />,
      label: <span style={{ color: "red" }}>Delete</span>,
      onClick: () => {
        open({
          icon: <ExclamationCircleFilled />,
          title: "Delete survey?",
          content: (
            <span>
              Are you sure you want to delete this survey? This action cannot be
              undone and the survey will be permanently removed.
            </span>
          ),
          cancelText: "Cancel",
          okText: "Delete",
          okButtonProps: { danger: true },
          onOk: () => surveyDelete.fetch(),
        });
      },
    },
  ];

  useMessageSuccess([surveyUpdate], "Changes saved");
  useMessageSuccess([surveyDelete], "Survey deleted");
  useMessageError([surveyUpdate]);
  useMessageError([templateCreate]);

  return (
    <SinglePageContainer
      title="Edit Survey"
      className="pagePadding"
      extra={
        <>
          <Button onClick={handleSaveTemplate} loading={templateCreate.loading}>
            Save as template
          </Button>
          <Button onClick={handleSave} loading={surveyUpdate.loading}>
            Save
          </Button>
          <Button type="primary" onClick={handlePublish}>
            Publish
          </Button>
          <Dropdown
            menu={{ items }}
            placement="bottomRight"
            trigger={["click"]}
          >
            <Button icon={<EllipsisOutlined />} />
          </Dropdown>
        </>
      }
      style={{ backgroundColor: "transparent" }}
    >
      <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
        <div style={{ width: 880 }}>
          <Segmented
            size="large"
            value={mode}
            onChange={(value) => handleModeChange(value as "add" | "preview")}
            options={[
              { label: "Edit", value: "add", icon: <EditOutlined /> },
              { label: "Preview", value: "preview", icon: <EyeOutlined /> },
            ]}
          />
          {mode === "add" ? (
            <div className={styles.editInfoWrapper}>
              <div className={styles.editInfo}>
                <Row gutter={[16, 0]}>
                  <Col span={6}>
                    <Text strong>ID</Text>
                  </Col>
                  <Col span={18}>
                    <Text>
                      {surveyData.jobs?.map((item) => item.jobId).join(", ") ||
                        "-"}
                    </Text>
                  </Col>
                  <Col span={6}>
                    <Text strong>Title</Text>
                  </Col>
                  <Col span={18}>
                    <Text>{surveyData.title}</Text>
                  </Col>

                  <Col span={6}>
                    <Text strong>Created by</Text>
                  </Col>
                  <Col span={18}>
                    {/* <Text>
                      {surveyData.user?.firstName} {surveyData.user?.lastName}
                    </Text> */}
                    <NavLink
                      to={`/users/${surveyData.user?.id}/edit`}
                      style={{ textDecoration: "underline" }}
                    >
                      {surveyData.user?.firstName} {surveyData.user?.lastName}
                    </NavLink>
                  </Col>

                  <Col span={6}>
                    <Text strong>Created at</Text>
                  </Col>
                  <Col span={18}>
                    <Text>
                      {dayjs(surveyData.createdAt).format(
                        `${dateFormat} ${timeFormatFull}`,
                      )}
                    </Text>
                  </Col>

                  <Col span={6}>
                    <Text strong>Status</Text>
                  </Col>
                  <Col span={18}>
                    {/* <Badge
                      color={getSurveyStatusColor(
                        surveyData.status as SurveyStatus,
                      )}
                      text={
                        <span className="capitalize-first-letter">
                          {surveyData.status}
                        </span>
                      }
                    /> */}
                    <StatusIndicator
                      color={getSurveyStatusColor(
                        surveyData.status as SurveyStatus,
                      )}
                      text={surveyData.status}
                    />
                  </Col>
                </Row>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <div style={{ marginTop: 24 }}>
        {mode === "add" && (
          <SurveyBuilder
            initialSurvey={surveyData}
            setInitialSurvey={setSurveyData}
            onChange={handleSurveyChange}
          />
        )}
        {mode === "preview" && (
          <SurveyPreview
            survey={surveyData}
            readOnly={false}
            onSubmit={(answers) => console.log("Answers:", answers)}
          />
        )}
        {surveyGetById.loading ||
          templateCreate.loading ||
          surveyUpdate.loading ? (
          <Loading absolute />
        ) : null}
      </div>
      {contextHolder}
    </SinglePageContainer>
  );
}
